import { API_BASE_URL } from '../config/api';
import { defaultAuthConfig } from '../types/auth';

export type NotificationRecord = {
  id: number;
  sendFrom?: number;
  sendTo: string;
  notificationBody: string;
  isOpenned?: boolean;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

export type CreateNotificationPayload = {
  sendTo: string;
  notificationBody: string;
};

type ApiEnvelope<T> = {
  ok?: boolean;
  data?: T;
  error?: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' ? (value as Record<string, unknown>) : {});

const asString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return undefined;
};

const toErrorMessage = (payload: unknown, fallback: string): string => {
  const record = asRecord(payload);
  const error = asRecord(record.error);
  return (
    asString(error.message) ||
    asString(record.message) ||
    asString(record.error) ||
    fallback
  );
};

const buildAuthHeaders = (withJson = false): HeadersInit => {
  const headers: Record<string, string> = { Accept: '*/*' };
  if (withJson) headers['Content-Type'] = 'application/json';

  if (typeof window === 'undefined') return headers;

  const token = window.localStorage.getItem(defaultAuthConfig.tokenKey);
  const rawUser = window.localStorage.getItem(defaultAuthConfig.userKey);

  if (token) headers.Authorization = `Bearer ${token}`;

  if (rawUser) {
    try {
      const parsedUser = asRecord(JSON.parse(rawUser));
      const userId = asString(parsedUser.id);
      const role = asString(parsedUser.role);
      if (userId) headers['x-user-id'] = userId;
      if (role) headers['x-user-role'] = role;
    } catch {
      // ignore
    }
  }

  return headers;
};

const normalizeNotification = (raw: unknown): NotificationRecord => {
  const record = asRecord(raw);
  return {
    id: asNumber(record.id) ?? 0,
    sendFrom: asNumber(record.sendFrom),
    sendTo: asString(record.sendTo) || '',
    notificationBody: asString(record.notificationBody) || '',
    isOpenned: typeof record.isOpenned === 'boolean' ? record.isOpenned : undefined,
    createdAt: asString(record.createdAt),
    updatedAt: asString(record.updatedAt),
    deletedAt: asString(record.deletedAt) ?? null,
  };
};

class NotificationService {
  private baseUrl: string;

  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async create(payload: CreateNotificationPayload): Promise<NotificationRecord> {
    const sendTo = payload.sendTo?.trim();
    const notificationBody = payload.notificationBody?.trim();
    if (!sendTo) throw new Error('sendTo is required.');
    if (!notificationBody) throw new Error('notificationBody is required.');

    const response = await fetch(`${this.baseUrl}/notifications`, {
      method: 'POST',
      headers: buildAuthHeaders(true),
      body: JSON.stringify({ sendTo, notificationBody }),
    });

    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }

    if (!response.ok) {
      throw new Error(toErrorMessage(parsed, `Request failed with status ${response.status}`));
    }

    const envelope = asRecord(parsed) as ApiEnvelope<unknown>;
    const data = (envelope && 'data' in envelope ? envelope.data : parsed) as unknown;
    return normalizeNotification(data);
  }

  async getAllAdmin(): Promise<NotificationRecord[]> {
    const response = await fetch(`${this.baseUrl}/notifications`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }

    if (!response.ok) {
      throw new Error(toErrorMessage(parsed, `Request failed with status ${response.status}`));
    }

    const record = asRecord(parsed) as ApiEnvelope<unknown>;
    const list = (record.data ?? asRecord(parsed).data) as unknown;
    const items = Array.isArray(list) ? list : [];
    return items.map(normalizeNotification);
  }

  async getMine(): Promise<NotificationRecord[]> {
    const response = await fetch(`${this.baseUrl}/notifications/mine`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }

    if (!response.ok) {
      throw new Error(toErrorMessage(parsed, `Request failed with status ${response.status}`));
    }

    const record = asRecord(parsed) as ApiEnvelope<unknown>;
    const list = (record.data ?? asRecord(parsed).data) as unknown;
    const items = Array.isArray(list) ? list : [];
    return items.map(normalizeNotification);
  }

  async getOne(notificationId: number): Promise<NotificationRecord> {
    if (!Number.isFinite(notificationId) || notificationId <= 0) {
      throw new Error('notificationId is required.');
    }

    const response = await fetch(`${this.baseUrl}/notifications/${notificationId}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }

    if (!response.ok) {
      throw new Error(toErrorMessage(parsed, `Request failed with status ${response.status}`));
    }

    const envelope = asRecord(parsed) as ApiEnvelope<unknown>;
    const data = (envelope && 'data' in envelope ? envelope.data : parsed) as unknown;
    return normalizeNotification(data);
  }

  async delete(notificationId: number): Promise<void> {
    if (!Number.isFinite(notificationId) || notificationId <= 0) {
      throw new Error('notificationId is required.');
    }

    const response = await fetch(`${this.baseUrl}/notifications/${notificationId}`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    });

    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }

    if (!response.ok) {
      throw new Error(toErrorMessage(parsed, `Request failed with status ${response.status}`));
    }
  }
}

export const notificationService = new NotificationService();
export { NotificationService };

